// Small presentational primitives shared across views: Card, StatCard, Badge,
// JsonView, Loading/ErrorState, ProgressBar.

import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import { CollapsibleSection } from './CollapsibleSection';
import { initials, colorFor } from '../lib/avatar';

// Small circular identity avatar: white INITIALS on a DETERMINISTIC colored
// background (derived from `colorKey`, falling back to the name). Reused by the
// userbar and anywhere a compact identity chip is wanted (see lib/avatar.ts).
export function Avatar({
  name,
  colorKey,
  size = 26,
}: {
  name?: string | null;
  colorKey?: string | null;
  size?: number;
}) {
  return (
    <span
      className="avatar"
      aria-hidden="true"
      style={{
        background: colorFor(colorKey ?? name ?? ''),
        width: size,
        height: size,
        fontSize: Math.round(size * 0.42),
      }}
    >
      {initials(name)}
    </span>
  );
}

// H42 — every titled card is collapsible by clicking its title (no separate button).
// Pass `collapsible={false}` to keep a card always-open, or `storageKey` to control the
// persistence key (otherwise it derives one from a string title). Untitled cards stay
// plain (nothing to click).
export function Card({
  title,
  actions,
  children,
  collapsible = true,
  storageKey,
  defaultCollapsed,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  collapsible?: boolean;
  storageKey?: string;
  defaultCollapsed?: boolean;
}) {
  if (title != null && collapsible) {
    const key = storageKey ?? (typeof title === 'string' ? `card.collapsed:${title}` : undefined);
    return (
      <CollapsibleSection title={title} actions={actions} storageKey={key} defaultCollapsed={defaultCollapsed}>
        {children}
      </CollapsibleSection>
    );
  }
  return (
    <section className="card">
      {(title || actions) && (
        <header className="card-head">
          <h2>{title}</h2>
          <div className="card-actions">{actions}</div>
        </header>
      )}
      <div className="card-body">{children}</div>
    </section>
  );
}

/**
 * A rollup figure. With `to`, the whole card is a LINK to the section that owns the
 * thing being counted (UX22) — "5 live providers" is a fact you immediately want to
 * act on, and the card was the one place on Overview that answered a question and
 * then made you find the page yourself.
 *
 * A card with no sensible destination stays a plain div rather than growing a link
 * to somewhere approximate.
 */
export function StatCard({
  label,
  value,
  sub,
  to,
  title,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  to?: string;
  title?: string;
}) {
  const body = (
    <>
      <div className="stat-value">{value}</div>
      <div className="stat-label">{label}</div>
      {sub && <div className="stat-sub">{sub}</div>}
    </>
  );
  if (!to) return <div className="stat-card">{body}</div>;
  return (
    <Link className="stat-card stat-card-link" to={to} title={title ?? `Open ${label}`}>
      {body}
    </Link>
  );
}

// (The OnlineStatus dot that used to live in the sidebar footer is gone: the
// footer went with it, and a failing request already says "offline" louder than a
// permanently-green dot ever did. The version it shared that row with moved up
// into the logo row.)

type Tone = 'ok' | 'warn' | 'bad' | 'info' | 'muted';

export function Badge({ tone = 'info', children }: { tone?: Tone; children: ReactNode }) {
  return <span className={`badge badge-${tone}`}>{children}</span>;
}

/** Map common status strings to a badge tone. */
export function statusTone(status: string): Tone {
  const s = status.toLowerCase();
  if (['online', 'active', 'approved', 'succeeded', 'idle'].includes(s)) return 'ok';
  if (['pending', 'queued', 'dispatched', 'running', 'opening', 'loading', 'draining', 'invited'].includes(s)) return 'warn';
  if (['offline', 'failed', 'rejected', 'cancelled', 'lost', 'errored', 'suspended', 'deleting'].includes(s)) return 'bad';
  return 'info';
}

export function StatusBadge({ status }: { status: string }) {
  return <Badge tone={statusTone(status)}>{status}</Badge>;
}

export function JsonView({ value }: { value: unknown }) {
  let text: string;
  try {
    text = JSON.stringify(value, null, 2);
  } catch {
    text = String(value);
  }
  return <pre className="json-view">{text}</pre>;
}

export function ProgressBar({ value }: { value: number }) {
  const pct = Math.max(0, Math.min(100, Math.round(value * 100)));
  return (
    <div className="progress" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} role="progressbar">
      <div className="progress-fill" style={{ width: `${pct}%` }} />
    </div>
  );
}

export function Loading({ what = 'Loading…' }: { what?: string }) {
  return <div className="loading">{what}</div>;
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="error-state">
      <span>⚠ {message}</span>
      {onRetry && (
        <button className="btn" onClick={onRetry}>
          Retry
        </button>
      )}
    </div>
  );
}

// Segmented control: a row of mutually-exclusive options (e.g. the Clients
// density filter Compact|Standard|Full). Controlled — the parent owns `value`.
export function Segmented<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label?: ReactNode;
  options: { value: T; label: ReactNode }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="segmented-wrap">
      {label && <span className="segmented-label">{label}</span>}
      <div className="segmented" role="tablist">
        {options.map((o) => (
          <button
            key={o.value}
            role="tab"
            aria-selected={o.value === value}
            className={`segmented-item ${o.value === value ? 'active' : ''}`}
            onClick={() => onChange(o.value)}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function bytesHuman(n: number): string {
  if (!n) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}
